import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const API_BASE = (typeof window !== 'undefined' && window.location.hostname === 'amino-chain.com')
  ? FRONTEND_ORIGIN
  : BACKEND_URL;
const API = `${API_BASE}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [customerTier, setCustomerTier] = useState(null); // { customer_type, custom_discount_percentage, minimum_order_amount }
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Set axios default header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch customer tier info when user is authenticated
  const fetchCustomerTier = useCallback(async (userId) => {
    try {
      const response = await axios.get(`${API}/users/customers/${userId}`);
      setCustomerTier({
        customer_type: response.data.customer_type || 'retail',
        custom_discount_percentage: response.data.custom_discount_percentage,
        minimum_order_amount: response.data.minimum_order_amount || 0
      });
    } catch (error) {
      // Default to retail if tier info can't be fetched
      setCustomerTier({ customer_type: 'retail', custom_discount_percentage: null, minimum_order_amount: 0 });
    }
  }, []);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
          // Fetch customer tier info for the logged-in user
          if (response.data?.id) {
            fetchCustomerTier(response.data.id);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          // Clear invalid token
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          setCustomerTier(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token, fetchCustomerTier]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      // Fetch customer tier info after login
      if (userData?.id) {
        fetchCustomerTier(userData.id);
      }
      
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  }, [fetchCustomerTier]);

  const register = useCallback(async (email, name, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, name, password });

      if (response.data?.access_token && response.data?.user) {
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        if (userData?.id) {
          fetchCustomerTier(userData.id);
        }
        return {
          success: true,
          requiresVerification: false,
          user: userData,
        };
      }

      return { 
        success: true, 
        requiresVerification: true,
        email: response.data.email,
        emailSent: response.data.email_sent,
        message: response.data.message
      };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      return { success: false, error: message };
    }
  }, [fetchCustomerTier]);

  const verifyEmail = useCallback(async (email, code) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { email, code });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Verification failed';
      return { success: false, error: message };
    }
  }, []);

  const resendVerification = useCallback(async (email) => {
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, { email });
      return { 
        success: true, 
        emailSent: response.data.email_sent,
        message: response.data.message 
      };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to resend';
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setCustomerTier(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const isAuthenticated = !!user;
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isWholesale = customerTier?.customer_type === 'wholesale';

  const value = {
    user,
    token,
    loading,
    login,
    register,
    verifyEmail,
    resendVerification,
    logout,
    isAuthenticated,
    isSuperAdmin,
    isAdmin,
    customerTier,
    isWholesale,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
